import { PortfolioGroupedItem } from "../../../screens/positions-and-orders/models/PortfolioGroupedItem";
import { positionsProcessor } from "../../../screens/positions-and-orders/utils/PositionsProcessor";
import { PortfolioModel } from "../../../screens/portfolio-info/types/PortfolioModel";
import { PortfolioSettingsModel } from "../../../screens/portfolio-info/types/PortfolioSettingsModel";
import { PortfolioDates } from "../../hooks/use-portfolio-service/PortfolioDates";
import { PortfolioProfile } from "../../types/PortfolioProfile";
import { PortfolioRecord } from "../../types/PortfolioRecord";
import { assetService } from "../AssetService";
import {
    portfolioPositionsCalculationService
} from "../portfolio-positions-calculation-service/PortfolioPositionsCalculationService";
import {
    portfolioBalanceCalculationService,
} from "../portfolio-balance-calculation-service/PortfolioBalanceCalculationService";

type BuildPortfolioProfileResult = {
    profile: PortfolioProfile;
    portfolioDates: PortfolioDates | null;
};

class PortfolioProfileService {
    buildProfile(portfolio: PortfolioModel | null): BuildPortfolioProfileResult {
        if (portfolio === null || portfolio.portfolioSettings === null) {
            return {
                profile: { data: [] },
                portfolioDates: null,
            };
        }

        const records: PortfolioRecord[] = [];
        let processedPrice = portfolio.portfolioSettings.minUnderlyingPrice;
        const positions = positionsProcessor.getSummarizedOrders(portfolio);
        const valuationDate = new Date();
        const portfolioDates = this.getPortfolioDates(
            positions,
            portfolio.portfolioSettings.isTripleCustomBalanceUsingEqualShifts,
            portfolio.portfolioSettings.selectedCustomDate,
            valuationDate,
        );

        while (processedPrice <= portfolio.portfolioSettings.maxUnderlyingPrice) {
            records.push(this.getPortfolioRecord(
                processedPrice,
                portfolio,
                positions,
                portfolio.portfolioSettings,
                portfolioDates,
                valuationDate,
            ));
            processedPrice += portfolio.portfolioSettings.calculationStep;
        }

        return {
            profile: { data: records },
            portfolioDates,
        };
    }

    buildComparedProfile(
        selectedPortfolio: PortfolioModel,
        comparedPortfolio: PortfolioModel | null,
    ): PortfolioProfile {
        if (
            comparedPortfolio === null ||
            selectedPortfolio.portfolioSettings === null ||
            comparedPortfolio.portfolioSettings === null ||
            !selectedPortfolio.portfolioSettings.useComparedPortfolio
        ) {
            return { data: [] };
        }

        const records: PortfolioRecord[] = [];
        let processedPrice = selectedPortfolio.portfolioSettings.minUnderlyingPrice;
        const positions = positionsProcessor.getSummarizedOrders(comparedPortfolio);
        const valuationDate = new Date();
        const portfolioDates = this.getPortfolioDates(
            positions,
            selectedPortfolio.portfolioSettings.isTripleCustomBalanceUsingEqualShifts,
            selectedPortfolio.portfolioSettings.selectedCustomDate,
            valuationDate,
        );

        while (processedPrice <= selectedPortfolio.portfolioSettings.maxUnderlyingPrice) {
            records.push(this.getPortfolioRecord(
                processedPrice,
                comparedPortfolio,
                positions,
                selectedPortfolio.portfolioSettings,
                portfolioDates,
                valuationDate,
            ));
            processedPrice += selectedPortfolio.portfolioSettings.calculationStep;
        }

        return { data: records };
    }

    private getPortfolioRecord(
        processedPrice: number,
        portfolio: PortfolioModel,
        positions: PortfolioGroupedItem[],
        portfolioSettings: PortfolioSettingsModel,
        portfolioDates: PortfolioDates,
        valuationDate: Date,
    ): PortfolioRecord {
        const hideCommitedBalance = portfolioSettings.hideCommitedBalance;
        const commitedBalance = portfolioSettings.commitedBalance;
        const isTripleCustomBalanceEnabled = portfolioSettings.isTripleCustomBalanceEnabled;

        const getBalance = (calculatedBalance: number): number =>
            hideCommitedBalance
                ? calculatedBalance - commitedBalance
                : calculatedBalance;

        const getImpliedVolatility = (item: PortfolioGroupedItem): number =>
            portfolioSettings.isCustomImpliedVolatilityEnabled
                ? item.customImpliedVolatility!
                : item.impliedVolatility!;

        const getValuationContext = (date: Date) => ({
            underlyingPrice: processedPrice,
            valuationDate: date,
            useCustomImpliedVolatility: portfolioSettings.isCustomImpliedVolatilityEnabled,
        });

        return {
            price: processedPrice,
            currentDateBalance: getBalance(portfolioBalanceCalculationService.calculateAtDate(
                portfolio,
                getValuationContext(valuationDate),
                portfolioSettings.includeFeesInBalance,
            )),
            customDateBalance1: portfolioDates.custom1 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom1 < portfolioDates.nearestExpiration
                ? getBalance(portfolioBalanceCalculationService.calculateAtDate(
                    portfolio,
                    getValuationContext(portfolioDates.custom1),
                    portfolioSettings.includeFeesInBalance,
                ))
                : 0,
            customDateBalance2: isTripleCustomBalanceEnabled && portfolioDates.custom2 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom2 < portfolioDates.nearestExpiration
                ? getBalance(portfolioBalanceCalculationService.calculateAtDate(
                    portfolio,
                    getValuationContext(portfolioDates.custom2),
                    portfolioSettings.includeFeesInBalance,
                ))
                : 0,
            customDateBalance3: isTripleCustomBalanceEnabled && portfolioDates.custom3 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom3 < portfolioDates.nearestExpiration
                ? getBalance(portfolioBalanceCalculationService.calculateAtDate(
                    portfolio,
                    getValuationContext(portfolioDates.custom3),
                    portfolioSettings.includeFeesInBalance,
                ))
                : 0,
            expirationDateBalance: getBalance(
                portfolioBalanceCalculationService.calculateAtExpiration(
                    portfolio,
                    processedPrice,
                    portfolioSettings.includeFeesInBalance,
                ),
            ),
            currentDelta: portfolioPositionsCalculationService.delta.calculateAtCurrent(positions, processedPrice),
            customDelta: portfolioDates.custom1 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom1 < portfolioDates.nearestExpiration
                ? portfolioPositionsCalculationService.delta.calculateAtCustom(positions, processedPrice, portfolioDates.custom1, getImpliedVolatility)
                : 0,
            expirationDelta: portfolioPositionsCalculationService.delta.calculateAtExpiration(positions, processedPrice),
            currentGamma: portfolioPositionsCalculationService.gamma.calculateAtCurrent(positions, processedPrice),
            customGamma: portfolioDates.custom1 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom1 < portfolioDates.nearestExpiration
                ? portfolioPositionsCalculationService.gamma.calculateAtCustom(positions, processedPrice, portfolioDates.custom1, getImpliedVolatility)
                : 0,
            expirationGamma: portfolioPositionsCalculationService.gamma.calculateAtExpiration(positions, processedPrice),
            currentVega: portfolioPositionsCalculationService.vega.calculateAtCurrent(positions, processedPrice),
            customVega: portfolioDates.custom1 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom1 < portfolioDates.nearestExpiration
                ? portfolioPositionsCalculationService.vega.calculateAtCustom(positions, processedPrice, portfolioDates.custom1, getImpliedVolatility)
                : 0,
            expirationVega: portfolioPositionsCalculationService.vega.calculateAtExpiration(positions, processedPrice),
            currentTheta: portfolioPositionsCalculationService.theta.calculateAtCurrent(positions, processedPrice),
            customTheta: portfolioDates.custom1 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom1 < portfolioDates.nearestExpiration
                ? portfolioPositionsCalculationService.theta.calculateAtCustom(positions, processedPrice, portfolioDates.custom1, getImpliedVolatility)
                : 0,
            expirationTheta: portfolioPositionsCalculationService.theta.calculateAtExpiration(positions, processedPrice),
            currentRho: portfolioPositionsCalculationService.rho.calculateAtCurrent(positions, processedPrice),
            customRho: portfolioDates.custom1 !== null && portfolioDates.nearestExpiration !== null && portfolioDates.custom1 < portfolioDates.nearestExpiration
                ? portfolioPositionsCalculationService.rho.calculateAtCustom(positions, processedPrice, portfolioDates.custom1, getImpliedVolatility)
                : 0,
            expirationRho: portfolioPositionsCalculationService.rho.calculateAtExpiration(positions, processedPrice),
        };
    }

    private getPortfolioDates(
        positions: PortfolioGroupedItem[],
        isTripleCustomBalanceUsingEqualShifts: boolean,
        customDate: Date,
        currentDate: Date,
    ): PortfolioDates {
        const customDate1 = customDate instanceof Date ? customDate : new Date(customDate);

        const expirations = positions
            .filter(item =>
                assetService.isOptionType(item.type) &&
                item.currentSize !== 0 &&
                !assetService.isOptionPositionExpired(item))
            .map(item => new Date(item.expireAt!).getTime());

        if (expirations.length === 0) {
            return {
                current: currentDate,
                custom1: null,
                custom2: null,
                custom3: null,
                nearestExpiration: null,
            };
        }

        const nearestExpiration = new Date(Math.min(...expirations));

        const { customDate2, customDate3 } = isTripleCustomBalanceUsingEqualShifts
            ? this.getExtraCustomDatesWithSameShifts(
                customDate1,
                nearestExpiration,
                currentDate,
            )
            : this.getExtraCustomDatesByProportionallyDividingRemainingTime(customDate1, nearestExpiration);

        return {
            current: currentDate,
            custom1: customDate1,
            custom2: customDate2,
            custom3: customDate3,
            nearestExpiration,
        };
    }

    private getExtraCustomDatesByProportionallyDividingRemainingTime(
        customDate1: Date,
        expiration: Date,
    ): {
        customDate2: Date;
        customDate3: Date;
    } {
        const equalShift = (expiration.getTime() - customDate1.getTime()) / 3;

        return {
            customDate2: new Date(customDate1.getTime() + equalShift),
            customDate3: new Date(customDate1.getTime() + equalShift * 2),
        };
    }

    private getExtraCustomDatesWithSameShifts(
        customDate1: Date,
        expiration: Date,
        currentDate: Date,
    ): {
        customDate2: Date;
        customDate3: Date;
    } {
        const initialShift = customDate1.getTime() - currentDate.getTime();

        return {
            customDate2: customDate1.getTime() + initialShift <= expiration.getTime()
                ? new Date(customDate1.getTime() + initialShift)
                : expiration,
            customDate3: customDate1.getTime() + initialShift * 2 <= expiration.getTime()
                ? new Date(customDate1.getTime() + initialShift * 2)
                : expiration,
        };
    }
}

export const portfolioProfileService = new PortfolioProfileService();
